/**
 * classification.ts — D-050-04-classification table (LOCKED in 050-CONTEXT.md).
 *
 * Per-block-type Content vs Design property classification for the Phase 050
 * ABox properties panel. Implementation MUST mirror the table verbatim:
 *
 *   | Block type        | Content                          | Design               |
 *   |-------------------|----------------------------------|----------------------|
 *   | text              | text                             | class                |
 *   | heading           | text                             | class, level         |
 *   | input             | placeholder, binding, label      | class                |
 *   | list              | iterate_over, itemVar            | class                |
 *   | generic/container | (none — `class` under Design)    | class                |
 *
 * Edge cases (CONTEXT D-050-04-classification edge-case bullets):
 *   - `input.binding` is Content (value-relationship, not style).
 *   - `list.iterate_over` is Content (says WHAT to iterate, not how to style).
 *   - For any unknown / new block-type, both `getContentKeys` and
 *     `getDesignKeys` return [] — the panel falls back to a single flat
 *     "Properties" section via `getFallbackKeys`. [ASSUMED-050-01] —
 *     carries forward until table is extended.
 */

export type AboxBlockType =
  | 'text'
  | 'heading'
  | 'input'
  | 'list'
  | 'generic'
  | 'container'

const CONTENT_KEYS: Record<string, readonly string[]> = {
  text: ['text'],
  heading: ['text'],
  input: ['placeholder', 'binding', 'label'],
  list: ['iterate_over', 'itemVar'],
  generic: [],
  container: [],
}

const DESIGN_KEYS: Record<string, readonly string[]> = {
  text: ['class'],
  heading: ['class', 'level'],
  input: ['class'],
  list: ['class'],
  generic: ['class'],
  container: ['class'],
}

export function getContentKeys(blockType: string): readonly string[] {
  return CONTENT_KEYS[blockType] ?? []
}

export function getDesignKeys(blockType: string): readonly string[] {
  return DESIGN_KEYS[blockType] ?? []
}

/**
 * For unknown block-types, return every property of the envelope as a
 * single flat list in declaration order — the panel renders this under a
 * single "Properties" section instead of the Content/Design split.
 */
export function getFallbackKeys(
  envelopeProperties: Record<string, unknown>,
): readonly string[] {
  return Object.keys(envelopeProperties)
}
